import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const aiJobDescriptionGenerator: FeatureContent = {
  slug: 'ai-job-description-generator',
  path: '/features/ai-job-description-generator',
  scorecardConnector:
    'This starts the workflow the scorecard is built from: a clear role spec the match scores against.',
  audience: 'company',
  icon: 'Briefcase',
  name: 'Job Assistant',
  stage: 1,
  navLabel: 'AI Job Description Generator',
  navDescription: 'Write a job ad candidates actually respond to, in under a minute.',
  primaryKeyword: 'job description generator',
  seo: {
    title: 'AI Job Description Generator | Talenture.ai',
    description:
      'AI job description generator that turns a few inputs into a complete, realistic job ad in under a minute. Structured, honest, and tuned to each seniority level.',
    keywords: [
      'job description generator',
      'AI job description writer',
      'job ad generator',
      'job posting generator',
      'write a job description',
      'AI job post',
    ],
    ogTitle: 'AI Job Description Generator | Talenture.ai',
    ogDescription:
      'Turn a title and a few skills into a high-converting job ad in under a minute. Structured, realistic, and tuned to the seniority of the role.',
  },
  hero: {
    eyebrow: 'Stage 1 of the hiring mission · Define the role',
    whereThisFits:
      'Every hiring mission starts here. The role you define is what Talenture matches candidates against in the next step.',
    h1: 'Write a job ad candidates actually respond to',
    painOpener: 'A blank page, a deadline, and a hiring manager waiting.',
    subhead:
      'Give Talenture a title and a few details. It returns a complete, realistic job description in under a minute, structured the way strong candidates expect to read it.',
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is an AI job description generator?',
    body: 'An AI job description generator turns a few inputs, like a title, key skills, and seniority, into a complete job ad. Talenture writes the opening hook, structures responsibilities and requirements, and tunes the tone to the role, so you publish a realistic post in minutes instead of starting from a blank page.',
  },
  sections: [
    {
      icon: 'PenLine',
      title: 'From a few inputs to a finished post',
      body: 'You provide the essentials. Talenture handles the writing, structure, and tone so the result reads like a real role, not a template.',
      bullets: [
        'Title, key skills, seniority, location, and work type',
        'A 300 to 500 word description with an opening hook',
        'Responsibilities grouped by technical, managerial, and strategic',
        'Realistic requirements plus inferred soft skills and benefits',
      ],
    },
    {
      icon: 'Mic',
      title: 'A tone that fits the role',
      body: 'A graduate role and a staff engineer role should not sound the same. Talenture adapts the voice to seniority and writes in the second person, so the ad speaks directly to the person you want to hire.',
    },
    {
      icon: 'Target',
      title: 'Realistic, not a wish list',
      body: 'Overstuffed requirements scare off strong candidates. Talenture keeps requirements honest and grounded in the role, which widens your qualified pool instead of shrinking it.',
    },
  ],
  visual: {
    component: 'JobDraftScreen',
    url: 'app.talenture.ai/roles/platform-engineer/description',
    caption: 'A draft you can still edit, with the requirements it proposes marked honest.',
  },
  supportingVisuals: [
    {
      component: 'JobDistributionScreen',
      url: 'app.talenture.ai/roles/platform-engineer/distribution',
      heading: 'The same draft, published',
      caption:
        'Approve the draft and it goes out to the boards from the same screen, with the spend and the applicants coming back in one place.',
    },
  ],
  proofPoints: [
    { value: 'Under 1 min', label: 'from inputs to a draft' },
    { value: '300 to 500', label: 'words, structured and ready' },
    { value: 'Second person', label: 'voice that speaks to candidates' },
  ],
  howTo: {
    heading: 'How to generate a job description',
    intro: 'Three steps from a blank page to a publishable role.',
    steps: [
      {
        title: 'Add the essentials',
        body: 'Enter the job title, a few key skills, the seniority level, and the location. You can paste an old post or describe the role in your own words.',
      },
      {
        title: 'Generate the draft',
        body: 'Talenture writes a complete description: an opening hook, grouped responsibilities, realistic requirements, and inferred soft skills and benefits.',
      },
      {
        title: 'Edit and publish',
        body: 'Adjust anything you want, then publish. From here the role can be distributed to job boards and applicants flow straight into your pipeline.',
      },
    ],
  },
  faq: [
    {
      question: 'How long does it take to write a job description?',
      answer:
        'Under a minute for a first draft. You supply a title, a few skills, seniority, and location, and Talenture returns a structured 300 to 500 word description you can edit and publish straight away.',
    },
    {
      question: 'Will the job ad sound generic?',
      answer:
        'No. The tone is tuned to the seniority of the role and written in the second person, so a junior post and a senior post read differently. Responsibilities are grouped and requirements are kept realistic rather than copied from a template.',
    },
    {
      question: 'Can I edit the generated description?',
      answer:
        'Yes. The output is a starting draft. You can change any section before publishing, then distribute the finished role to job boards directly from Talenture.',
    },
    {
      question: 'Does it help attract more candidates?',
      answer:
        'A clear, realistic, well-structured ad converts better than a wall of requirements. By keeping requirements honest and the tone engaging, Talenture helps you widen the qualified pool rather than filter it out before anyone applies.',
    },
  ],
  relatedSlugs: ['ai-candidate-matching', 'job-distribution', 'interview-scheduling'],
};
